import React, { useState } from 'react';
import {
    ArrowLeft, Mail, FileText,
    User as UserIcon, Shield, Loader2, Download,
    Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../api/config';
import { formatDate } from '../../../utils/stringUtils';

const ExitRequestDetails = ({ requestId, onBack, onApprove, onReject, onWithdraw, hideBack = false }) => {
    const { user } = useAuth();
    const [actionModal, setActionModal] = useState(null); // { type: 'approve' | 'reject', role: 'manager' | 'hr' }
    const [reasonInput, setReasonInput] = useState('');

    const { data: request, isLoading, error } = useQuery({
        queryKey: ['exitRequest', requestId],
        queryFn: async () => {
            // Correct endpoint is /api/exit-test/:id based on ExitRequests.jsx usage
            const response = await fetch(`${API_BASE_URL}/exit-test/${requestId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch request details');
            }
            const result = await response.json();
            return result.data;
        },
        enabled: !!requestId,
    });

    if (isLoading) {
        return (
            <div className={`flex items-center justify-center ${!hideBack ? 'min-h-screen bg-[#f4f6f9]' : 'py-10'}`}>
                <Loader2 className="w-8 h-8 text-[#0F2942] animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className={!hideBack ? "min-h-screen bg-[#f4f6f9] p-6" : "p-4"}>
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    <strong className="font-bold">Error!</strong>
                    <span className="block sm:inline"> {error.message}</span>
                    <button onClick={onBack} className="mt-2 text-sm underline">Back to List</button>
                </div>
            </div>
        );
    }

    if (!request) return null;

    // Permission logic
    const isSuperAdmin = user?.role === 'super_admin';

    // Withdrawn state — all actions must be locked
    const isWithdrawn = request.status === 'withdrawed' || request.status === 'withdrawn';

    // Helper to safely compare IDs (handles strings, objects, _id vs id)
    const compareIds = (source, target) => {
        if (!source || !target) return false;
        const sId = typeof source === 'object' ? (source._id || source.id) : source;
        const tId = typeof target === 'object' ? (target._id || target.id) : target;
        return String(sId) === String(tId);
    };

    const isManagerOfRequest =
        compareIds(request.managerApproval?.managerId, user) ||
        compareIds(request.employeeId?.employmentInfo?.reportingManager?.user, user) ||
        (request.managerEmail && user?.email && String(request.managerEmail).toLowerCase() === String(user.email).toLowerCase());

    const canManagerAction = isSuperAdmin || isManagerOfRequest;
    const isManagerPending = (request.managerApproval?.approvalStatus || 'pending') === 'pending';

    const isHRAdmin = isSuperAdmin || user?.role === 'hr_manager';
    const isHRPending = (request.hrApproval?.approvalStatus || 'pending') === 'pending';

    // Helper for badges
    const getStatusBadge = (status) => {
        const s = status?.toLowerCase() || 'pending';
        const styles = {
            approved: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
            rejected: 'bg-red-50 text-red-700 border border-red-100',
            pending: 'bg-amber-50 text-amber-700 border border-amber-100',
            submitted: 'bg-blue-50 text-[#0B2033] border border-blue-100',
            withdrawn: 'bg-gray-100 text-gray-600 border border-gray-200'
        };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize tracking-wider ${styles[s] || styles.pending}`}>
                {s}
            </span>
        );
    };

    const SectionTitle = ({ children }) => (
        <div className="font-bold mb-4 text-gray-700 text-sm capitalize tracking-wider border-b border-gray-100 pb-2">
            {children}
        </div>
    );

    const InfoBox = ({ label, value, fullWidth = false, className = '' }) => (
        <div className={`bg-gray-50/80 p-3 rounded-lg border border-gray-100 ${fullWidth ? 'col-span-full' : ''} ${className}`}>
            <div className="text-xs font-bold text-gray-500 capitalize tracking-wide mb-1">{label}</div>
            <div className="text-sm font-medium text-gray-900 break-words whitespace-pre-wrap first-letter:capitalize">{value || 'N/A'}</div>
        </div>
    );

    const ensureFilenameHasExtension = (filename, mimeType = '') => {
        if (!filename) return 'attachment';

        const mimeExtensionMap = {
            'application/pdf': '.pdf',
            'image/jpeg': '.jpg',
            'image/png': '.png',
            'image/webp': '.webp',
        };

        if (/\.[a-z0-9]+$/i.test(filename)) {
            return filename;
        }

        return `${filename}${mimeExtensionMap[mimeType] || ''}`;
    };

    const triggerBrowserDownload = (url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadAttachment = async (file, index) => {
        if (!file?.filePath) {
            return;
        }

        const fallbackName = file.originalName || `Document ${index + 1}`;

        try {
            const response = await fetch(file.filePath);
            if (!response.ok) {
                throw new Error('Failed to fetch attachment');
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const finalName = ensureFilenameHasExtension(fallbackName, blob.type);

            triggerBrowserDownload(blobUrl, finalName);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch {
            const token = localStorage.getItem('authToken') || 
                          localStorage.getItem('auth_token') || 
                          localStorage.getItem('token') || 
                          sessionStorage.getItem('authToken') || 
                          sessionStorage.getItem('auth_token') || 
                          sessionStorage.getItem('token');
            
            const getS3KeyFromUrl = (url) => {
                if (!url) return '';
                try {
                    const parsed = new URL(url);
                    return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
                } catch (e) {
                    return url;
                }
            };
            
            if (file.filePath && (file.filePath.includes('cloudinary.com') || file.filePath.startsWith('http'))) {
                const s3Key = getS3KeyFromUrl(file.filePath);
                const resolvedFilename = ensureFilenameHasExtension(fallbackName, file.mimeType || '');
                const downloadUrl = `${API_BASE_URL}/upload/s3Uploader/download?public_id=${encodeURIComponent(s3Key)}&filename=${encodeURIComponent(resolvedFilename)}&token=${token}`;
                triggerBrowserDownload(downloadUrl, resolvedFilename);
            } else {
                triggerBrowserDownload(file.filePath, ensureFilenameHasExtension(fallbackName));
            }
        }
    };

    const handleConfirmAction = () => {
        if (actionModal.type === 'approve') {
            onApprove(request, actionModal.role, reasonInput);
        } else {
            onReject(request, actionModal.role, reasonInput);
        }
        setActionModal(null);
        setReasonInput('');
    };


    return (
        <div className={hideBack ? "" : "min-h-screen bg-[#f4f6f9] p-6"}>
            <div className={hideBack ? "space-y-5" : "max-w-7xl mx-auto space-y-5"}>

                {/* Navigation */}
                {!hideBack && onBack && (
                    <button
                        onClick={onBack}
                        className="flex items-center text-gray-600 hover:text-[#0F2942] transition-colors mb-2 font-medium"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to List
                    </button>
                )}

                {/* HEADER */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-16 -mt-16 opacity-50 blur-2xl"></div>
                    <div className="relative z-10">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">Exit Request Details</h2>
                        <div className="flex flex-wrap items-center gap-6 text-sm">
                            <div className="flex flex-col">
                                <span className="text-xs text-gray-500 font-bold capitalize">Request ID</span>
                                <span className="font-medium text-gray-900">{request.requestId || 'EXIT-####'}</span>
                            </div>
                            <div className="h-8 w-px bg-gray-200 mx-2 hidden md:block"></div>
                            <div className="flex flex-col">
                                <span className="text-xs text-gray-500 font-bold capitalize">Submitted On</span>
                                <span className="font-medium text-gray-900">{formatDate(request.createdAt)}</span>
                            </div>
                            <div className="h-8 w-px bg-gray-200 mx-2 hidden md:block"></div>
                            <div className="flex flex-col">
                                <span className="text-xs text-gray-500 font-bold capitalize">SLA Pending</span>
                                <span className="font-medium text-gray-900">{request.daysRemaining || 'N/A'} Days</span>
                            </div>
                            <div className="ml-auto">
                                {getStatusBadge(request.status)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* WITHDRAWN BANNER */}
                {isWithdrawn && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-start gap-4">
                        <div className="shrink-0 mt-0.5 p-2 bg-slate-200 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-1">Resignation Withdrawn</p>
                            <p className="text-xs text-slate-500 font-medium mb-2">
                                This resignation request was withdrawn on{' '}
                                <span className="font-bold text-slate-700">{formatDate(request.withdrawal?.timestamp)}</span>.
                                All actions are now disabled.
                            </p>
                            {request.withdrawal?.remark && (
                                <div className="mt-2 bg-white border border-slate-200 rounded-lg px-4 py-3">
                                    <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Withdrawal Reason</span>
                                    <p className="text-sm text-slate-700 font-medium break-words whitespace-pre-wrap">
                                        &ldquo;{request.withdrawal.remark}&rdquo;
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* EMPLOYEE INFORMATION */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60">
                    <SectionTitle>Employee Information</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <InfoBox label="Name" value={request.employeeName} />
                        <InfoBox label="Email" value={request.employeeEmail} />
                        <InfoBox label="Employee ID" value={request.employeeId?.employeeId || request.employeeId?._id || request.employeeId} />
                        <InfoBox label="Department" value={request.department} />
                        <InfoBox label="Position" value={request.position} />
                        <InfoBox label="Date of Joining" value={request.joinDate ? formatDate(request.joinDate, { includeTime: false }) : 'N/A'} />
                        <InfoBox label="Tenure" value={request.tenure || 'N/A'} />
                    </div>
                </div>

                {/* REPORTING MANAGER INFORMATION */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60">
                    <SectionTitle>Reporting Manager Information</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <InfoBox label="Reporting Manager" value={request.managerName || 'N/A'} />
                        <InfoBox label="Manager Emp ID" value={request.managerEmployeeId || 'N/A'} />
                        <InfoBox label="Manager Email" value={request.managerEmail || 'N/A'} />
                        <InfoBox label="Manager Dept" value={request.managerDepartment || 'N/A'} />
                    </div>
                </div>

                {/* RESIGNATION DETAILS */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60">
                    <SectionTitle>Resignation Submission Details</SectionTitle>
                    <div className="space-y-4">
                        <InfoBox label="Resignation Category" value={request.resignationCategory} />
                        <InfoBox label="Brief Reason" value={request.reason} />
                        <InfoBox label="Detailed Statement" value={request.detailedReason} fullWidth />
                        <InfoBox label="Handover Notes" value={request.handoverNotes} fullWidth />

                        <div className="bg-gray-50/80 p-4 rounded-lg border border-gray-100">
                            <div className="text-xs font-bold text-gray-500 capitalize tracking-wide mb-3">Attachments</div>
                            {request.attachments && request.attachments.length > 0 ? (
                                <div className="space-y-2">
                                    {request.attachments.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2.5 bg-blue-50 text-[#0F2942] rounded-xl group-hover:bg-[#0F2942] group-hover:text-white transition-colors">
                                                    <FileText className="w-5 h-5" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-gray-800">{file.originalName || `Document ${idx + 1}`}</span>
                                                    <span className="text-[10px] text-gray-400 font-medium">Attached Attachment</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleDownloadAttachment(file, idx)}
                                                    className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                                    title="Download"
                                                >
                                                    <Download className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 bg-white/50 rounded-xl border border-dashed border-gray-200">
                                    <span className="text-gray-400 text-sm font-medium italic">No attachments found</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* NOTICE GOVERNANCE */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60">
                    <SectionTitle>Notice Governance</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <InfoBox label="Notice Period (Policy)" value={`${request.noticePeriod || 30} Days`} />
                        <InfoBox label="Notice Start Date" value={formatDate(request.submittedDate, { includeTime: false })} />
                        <InfoBox label="Notice End Date" value={formatDate(request.noticeEndDate, { includeTime: false })} />
                        <InfoBox label="Proposed Last Working Date" value={formatDate(request.proposedLastWorkingDate || request.lastWorkingDay, { includeTime: false })} />
                        <InfoBox
                            label="Notice Shortfall"
                            value={`${(() => {
                                if (!request.proposedLastWorkingDate || !request.resignationDate) return 0;
                                const start = new Date(request.resignationDate);
                                const end = new Date(request.proposedLastWorkingDate);
                                const served = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                                const policy = request.noticePeriod || 30;
                                const shortfall = policy - served;
                                return shortfall > 0 ? shortfall : 0;
                            })()} Days`}
                        />
                        <InfoBox label="Buyout Requested" value={request.buyoutRequest || 'No'} />
                        <InfoBox label="Buyout Reason" value={request.buyoutReason || 'Not Applicable'} />
                    </div>
                </div>

                {/* EMERGENCY CONTACT */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60">
                    <SectionTitle>Emergency Contact</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <InfoBox label="Name" value={request.emergencyContact?.name} />
                        <InfoBox label="Phone" value={request.emergencyContact?.phone} />
                        <InfoBox label="Email" value={request.emergencyContact?.email} />
                        <InfoBox label="Relationship" value={request.emergencyContact?.relationship} />
                    </div>
                </div>

                {/* DECLARATIONS */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60">
                    <SectionTitle>Employee Declarations</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InfoBox label="Voluntary Confirmation" value={request.declarations?.confirmVoluntary ? 'Yes' : 'No'} />
                        <InfoBox label="Policy Acknowledgement" value={request.declarations?.confirmPolicy ? 'Yes' : 'No'} />
                        <InfoBox label="Information Accuracy" value={request.declarations?.confirmAccurate ? 'Yes' : 'No'} />
                        <InfoBox label="Exit Interview Consent" value={request.declarations?.willingToParticipateInExitInterview ? 'Yes' : 'No'} />
                    </div>
                </div>

                {/* APPROVAL Status */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60">
                    <SectionTitle>Approval Status</SectionTitle>

                    {/* Manager Approval Block */}
                    <div className="mb-8 p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 text-gray-500">
                                    <UserIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Manager Approval</h4>
                                    <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mt-0.5">Primary Transition Review</p>
                                </div>
                            </div>
                            {getStatusBadge(request.managerApproval?.approvalStatus || 'pending')}
                        </div>

                        <div className={`mb-6 ${request.managerApproval?.approvalStatus !== 'pending' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : ''}`}>
                            {request.managerApproval?.approvalStatus !== 'pending' ? (
                                <>
                                    <InfoBox label="Manager Name" value={request.managerApproval?.name || request.managerName || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Employee ID" value={request.managerApproval?.employeeId || request.managerApproval?.managerId?.employeeId || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Work Email" value={request.managerApproval?.email || request.managerApproval?.managerId?.email || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Department" value={request.managerApproval?.department || request.managerApproval?.managerId?.department || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Review Date" value={formatDate(request.managerApproval?.timestamp)} className="bg-white shadow-sm border-gray-50" />
                                </>
                            ) : (
                                <div className="flex items-center bg-amber-50/50 border border-amber-100/50 rounded-xl px-4 py-3 text-[10px] text-amber-700 font-bold uppercase tracking-widest">
                                    <Clock className="w-4 h-4 mr-2.5 text-amber-400" />
                                    Awaiting reporting manager review
                                </div>
                            )}
                        </div>

                        {request.managerApproval?.approvalReason && (
                            <div className="mb-6 p-4 bg-emerald-50/30 rounded-xl border border-emerald-100">
                                <span className="block text-[10px] font-bold text-emerald-800 uppercase tracking-widest mb-2">Manager Approval Remark</span>
                                <p className="text-sm text-emerald-700 font-medium leading-relaxed break-words whitespace-pre-wrap">{request.managerApproval.approvalReason}</p>
                            </div>
                        )}

                        {request.managerApproval?.rejectedReason && (
                            <div className="mb-6 p-4 bg-red-50/30 rounded-xl border border-red-100">
                                <span className="block text-[10px] font-bold text-red-800 uppercase tracking-widest mb-2">Manager Rejection Reason</span>
                                <p className="text-sm text-red-700 font-medium leading-relaxed break-words whitespace-pre-wrap">{request.managerApproval.rejectedReason}</p>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 pt-2 max-w-md">
                            <button
                                onClick={() => setActionModal({ type: 'approve', role: 'manager' })}
                                disabled={isWithdrawn || !canManagerAction || !isManagerPending}
                                className={`flex-1 px-5 py-3 text-white text-xs font-bold rounded-xl transition-all shadow-lg ${isWithdrawn || !canManagerAction || !isManagerPending
                                    ? 'bg-gray-200 text-gray-400 shadow-none cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-100 hover:translate-y-[-1px] active:translate-y-0'
                                    } tracking-widest`}
                            >
                                Approve Request
                            </button>
                            <button
                                onClick={() => setActionModal({ type: 'reject', role: 'manager' })}
                                disabled={isWithdrawn || !canManagerAction || !isManagerPending}
                                className={`flex-1 px-5 py-3 text-white text-xs font-bold rounded-xl transition-all shadow-lg ${isWithdrawn || !canManagerAction || !isManagerPending
                                    ? 'bg-gray-200 text-gray-400 shadow-none cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-500 shadow-red-100 hover:translate-y-[-1px] active:translate-y-0'
                                    } tracking-widest`}
                            >
                                Reject Request
                            </button>
                        </div>
                        {!canManagerAction && isManagerPending && (
                            <div className="mt-4 flex items-center gap-2 text-[10px] text-amber-600 font-bold bg-amber-50 p-2.5 rounded-lg border border-amber-100 uppercase tracking-wide">
                                <Shield className="w-3.5 h-3.5" />
                                Restricted: Only assigned reporting manager can action
                            </div>
                        )}
                    </div>

                    {/* HR Approval Block */}
                    <div className="p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 text-gray-500">
                                    <Shield className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wide">HR Approval</h4>
                                    <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mt-0.5">Final Organizational Compliance</p>
                                </div>
                            </div>
                            {getStatusBadge(request.hrApproval?.approvalStatus || 'pending')}
                        </div>

                        <div className={`mb-6 ${request.hrApproval?.approvalStatus !== 'pending' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : ''}`}>
                            {request.hrApproval?.approvalStatus !== 'pending' ? (
                                <>
                                    <InfoBox label="HR Name" value={request.hrApproval?.name || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Employee ID" value={request.hrApproval?.employeeId || request.hrApproval?.managerId?.employeeId || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Work Email" value={request.hrApproval?.email || request.hrApproval?.managerId?.email || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Department" value={request.hrApproval?.department || request.hrApproval?.managerId?.department || 'N/A'} className="bg-white shadow-sm border-gray-50" />
                                    <InfoBox label="Compliance Date" value={formatDate(request.hrApproval?.timestamp)} className="bg-white shadow-sm border-gray-50" />
                                </>
                            ) : (
                                <div className="flex items-center bg-amber-50/50 border border-amber-100/50 rounded-xl px-4 py-3 text-[10px] text-amber-700 font-bold uppercase tracking-widest">
                                    <Clock className="w-4 h-4 mr-2.5 text-amber-400" />
                                    Awaiting compliance verification
                                </div>
                            )}
                        </div>

                        {request.hrApproval?.approvalReason && (
                            <div className="mb-6 p-4 bg-emerald-50/30 rounded-xl border border-emerald-100">
                                <span className="block text-[10px] font-bold text-emerald-800 uppercase tracking-widest mb-2">HR Approval Remark</span>
                                <p className="text-sm text-emerald-700 font-medium leading-relaxed break-words whitespace-pre-wrap">{request.hrApproval.approvalReason}</p>
                            </div>
                        )}

                        {request.hrApproval?.rejectedReason && (
                            <div className="mb-6 p-4 bg-red-50/30 rounded-xl border border-red-100">
                                <span className="block text-[10px] font-bold text-red-800 uppercase tracking-widest mb-2">HR Rejection Reason</span>
                                <p className="text-sm text-red-700 font-medium leading-relaxed break-words whitespace-pre-wrap">{request.hrApproval.rejectedReason}</p>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 pt-2 max-w-md">
                            <button
                                onClick={() => setActionModal({ type: 'approve', role: 'hr' })}
                                disabled={isWithdrawn || !isHRAdmin || !isHRPending}
                                className={`flex-1 px-5 py-3 text-white text-xs font-bold rounded-xl transition-all shadow-lg ${isWithdrawn || !isHRAdmin || !isHRPending
                                    ? 'bg-gray-200 text-gray-400 shadow-none cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-100 hover:translate-y-[-1px] active:translate-y-0'
                                    } tracking-widest`}
                            >
                                Approve Request
                            </button>
                            <button
                                onClick={() => setActionModal({ type: 'reject', role: 'hr' })}
                                disabled={isWithdrawn || !isHRAdmin || !isHRPending}
                                className={`flex-1 px-5 py-3 text-white text-xs font-bold rounded-xl transition-all shadow-lg ${isWithdrawn || !isHRAdmin || !isHRPending
                                    ? 'bg-gray-200 text-gray-400 shadow-none cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-500 shadow-red-100 hover:translate-y-[-1px] active:translate-y-0'
                                    } tracking-widest`}
                            >
                                Reject Request
                            </button>
                        </div>
                        {!isHRAdmin && isHRPending && (
                            <div className="mt-4 flex items-center gap-2 text-[10px] text-amber-600 font-bold bg-amber-50 p-2.5 rounded-lg border border-amber-100 uppercase tracking-wide">
                                <Shield className="w-3.5 h-3.5" />
                                Restricted: Accessible to HR Management only
                            </div>
                        )}
                    </div>

                    <hr className="my-8 border-gray-100" />

                    {/* Withdrawal Control — hidden when already withdrawn */}
                    {!isWithdrawn && (
                        <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                            <strong className="block text-gray-800 text-sm mb-4 font-bold uppercase tracking-wider">Withdrawal Control</strong>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => !request.allowWithdrawal && onWithdraw && onWithdraw(request, false)}
                                    disabled={request.allowWithdrawal}
                                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl transition-all shadow-lg ${request.allowWithdrawal
                                        ? 'bg-emerald-500 text-white cursor-default shadow-emerald-100'
                                        : 'bg-amber-500 text-white hover:bg-amber-600 shadow-amber-50 active:scale-95'
                                        }`}
                                >
                                    {request.allowWithdrawal ? (
                                        <>
                                            <CheckCircle className="h-4 w-4" />
                                            Withdrawal Permission Granted
                                        </>
                                    ) : (
                                        'Allow Employee Withdrawal'
                                    )}
                                </button>
                            </div>
                            {request.allowWithdrawal && (
                                <p className="mt-3 text-xs text-emerald-600 font-medium flex items-center gap-1.5 ml-1">
                                    <AlertCircle className="h-3 w-3" />
                                    Employee can now withdraw this request from their profile regardless of approval status.
                                </p>
                            )}
                        </div>
                    )}

                </div>

                {/* MODAL PORTAL */}
                {actionModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 !m-0">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-in zoom-in-95 duration-200">
                            <div className={`p-6 ${actionModal.type === 'reject' ? 'bg-red-50' : 'bg-emerald-50'} border-b border-gray-100`}>
                                <h3 className={`text-xl font-bold ${actionModal.type === 'reject' ? 'text-red-700' : 'text-emerald-700'} capitalize`}>
                                    {actionModal.type} Exit Request
                                </h3>
                                <p className="text-gray-600 text-sm mt-1 first-letter:capitalize">
                                    Are you sure you want to {actionModal.type} this request?
                                </p>
                            </div>
                            <div className="p-6 space-y-4 text-left">
                                <label className="block text-sm font-bold text-gray-700 mb-2 capitalize">
                                    Remark <span className="text-red-500">*</span>
                                </label>
                                <div className="space-y-1">
                                    <textarea
                                        value={reasonInput}
                                        onChange={(e) => setReasonInput(e.target.value.slice(0, 500))}
                                        maxLength={1500}
                                        className={`w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 min-h-[140px] transition-all text-sm outline-none resize-none ${actionModal.type === 'reject' ? 'focus:ring-red-500/10 focus:border-red-500' : 'focus:ring-emerald-500/10 focus:border-emerald-500'
                                            }`}
                                        placeholder={`Please provide a remark for ${actionModal.type === 'reject' ? 'rejection' : 'approval'}...`}
                                        autoFocus
                                    />
                                    <div className="flex justify-end pr-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                            {reasonInput.length} / 1500
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => {
                                            setActionModal(null);
                                            setReasonInput('');
                                        }}
                                        className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-all text-sm active:scale-95"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmAction}
                                        disabled={!reasonInput.trim()}
                                        className={`flex-1 px-4 py-3 text-white font-bold rounded-xl shadow-lg transition-all text-sm active:scale-95 ${actionModal.type === 'reject'
                                            ? 'bg-red-600 hover:bg-red-700 shadow-red-100 disabled:opacity-50 disabled:shadow-none cursor-pointer disabled:cursor-not-allowed'
                                            : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100 cursor-pointer'
                                            }`}
                                    >
                                        Confirm {actionModal.type}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* AUDIT TIMELINE */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200/60 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 rounded-full -mr-16 -mt-16 opacity-50 blur-3xl"></div>
                    <SectionTitle>Audit Timeline & History</SectionTitle>
                    <div className="relative pl-8 space-y-8 ml-2 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                        {/* Draft Created */}
                        <div className="relative">
                            <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-gray-300 shadow-sm flex items-center justify-center">
                                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                            </div>
                            <div className="bg-gray-50/50 p-3 rounded-xl border border-gray-100 inline-block min-w-[240px]">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Process Initiated</p>
                                <p className="text-sm text-gray-900 font-semibold mb-0.5">Resignation Draft Created</p>
                                <p className="text-[10px] text-gray-400 font-medium">{formatDate(request.createdAt)}</p>
                            </div>
                        </div>

                        {/* Submitted */}
                        <div className="relative">
                            <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-blue-500 shadow-sm flex items-center justify-center">
                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                            </div>
                            <div className="bg-blue-50/30 p-3 rounded-xl border border-blue-100/50 inline-block min-w-[240px]">
                                <p className="text-[10px] font-bold text-[#0F2942] uppercase tracking-widest mb-1">Submission</p>
                                <p className="text-sm text-gray-900 font-semibold mb-0.5">Application Submitted</p>
                                <p className="text-[10px] text-gray-500 font-medium">{formatDate(request.submittedDate)}</p>
                            </div>
                        </div>

                        {/* Manager Action */}
                        {request.managerApproval?.timestamp && (
                            <div className="relative">
                                <div className={`absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 ${request.managerApproval.approvalStatus === 'rejected' ? 'border-rose-500' : 'border-emerald-500'} shadow-sm flex items-center justify-center`}>
                                    <div className={`w-1.5 h-1.5 ${request.managerApproval.approvalStatus === 'rejected' ? 'bg-rose-500' : 'bg-emerald-500'} rounded-full`}></div>
                                </div>
                                <div className={`${request.managerApproval.approvalStatus === 'rejected' ? 'bg-rose-50/30 border-rose-100/50' : 'bg-emerald-50/30 border-emerald-100/50'} p-3 rounded-xl border inline-block min-w-[240px]`}>
                                    <p className={`text-[10px] font-bold ${request.managerApproval.approvalStatus === 'rejected' ? 'text-rose-600' : 'text-emerald-600'} uppercase tracking-widest mb-1`}>Manager Review</p>
                                    <p className="text-sm text-gray-900 font-semibold mb-0.5">
                                        Request {request.managerApproval.approvalStatus === 'rejected' ? 'Rejected' : 'Approved'}
                                    </p>
                                    <p className="text-[11px] text-gray-600 font-medium">By {request.managerApproval.name || request.managerName || 'Reporting Manager'}</p>
                                    <p className="text-[10px] text-gray-400 font-medium mt-1">{formatDate(request.managerApproval.timestamp)}</p>
                                </div>
                            </div>
                        )}

                        {/* HR Action */}
                        {request.hrApproval?.timestamp && (
                            <div className="relative">
                                <div className={`absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 ${request.hrApproval.approvalStatus === 'rejected' ? 'border-rose-500' : 'border-purple-500'} shadow-sm flex items-center justify-center`}>
                                    <div className={`w-1.5 h-1.5 ${request.hrApproval.approvalStatus === 'rejected' ? 'bg-rose-500' : 'bg-purple-500'} rounded-full`}></div>
                                </div>
                                <div className={`${request.hrApproval.approvalStatus === 'rejected' ? 'bg-rose-50/30 border-rose-100/50' : 'bg-purple-50/30 border-purple-100/50'} p-3 rounded-xl border inline-block min-w-[240px]`}>
                                    <p className={`text-[10px] font-bold ${request.hrApproval.approvalStatus === 'rejected' ? 'text-rose-600' : 'text-purple-600'} uppercase tracking-widest mb-1`}>HR Compliance</p>
                                    <p className="text-sm text-gray-900 font-semibold mb-0.5">
                                        Final Action: {request.hrApproval.approvalStatus === 'rejected' ? 'Rejected' : 'Approved'}
                                    </p>
                                    <p className="text-[11px] text-gray-600 font-medium">By {request.hrApproval.name || 'HR Manager'}</p>
                                    <p className="text-[10px] text-gray-400 font-medium mt-1">{formatDate(request.hrApproval.timestamp)}</p>
                                </div>
                            </div>
                        )}

                        {/* Withdrawal Permission */}
                        {request.allowWithdrawal && !request.withdrawal?.timestamp && (
                            <div className="relative">
                                <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-amber-500 shadow-sm flex items-center justify-center">
                                    <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></div>
                                </div>
                                <div className="bg-amber-50/30 p-3 rounded-xl border border-amber-100/50 inline-block min-w-[240px]">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">Withdrawal Control</p>
                                    <p className="text-sm text-gray-900 font-semibold mb-0.5">Withdrawal Option Enabled</p>
                                    <p className="text-[10px] text-gray-500 font-medium leading-relaxed max-w-[300px]">HR has granted permission for the employee to withdraw this resignation request.</p>
                                </div>
                            </div>
                        )}

                        {/* Withdrawn */}
                        {request.withdrawal?.timestamp && (
                            <div className="relative">
                                <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-slate-500 shadow-sm flex items-center justify-center">
                                    <div className="w-1.5 h-1.5 bg-slate-500 rounded-full"></div>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 inline-block min-w-[240px]">
                                    <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Final Status</p>
                                    <p className="text-sm text-gray-900 font-semibold mb-0.5">Resignation Withdrawn</p>
                                    {request.withdrawal.remark && (
                                        <p className="text-[11px] text-slate-500 italic mt-1 max-w-[250px] break-words">"{request.withdrawal.remark}"</p>
                                    )}
                                    <p className="text-[10px] text-gray-400 font-medium mt-1">{formatDate(request.withdrawal.timestamp)}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ExitRequestDetails;
